export const MEDIA_ALPHA_OPAQUE = 'opaque'
export const MEDIA_ALPHA_CUTOUT = 'cutout'
export const MEDIA_ALPHA_SOFT = 'soft'
export const MEDIA_ALPHA_UNKNOWN = 'unknown'

const MAX_SCAN_PIXELS = 12000000

const normalizeMime = (mime) => `${mime || ''}`.split(';')[0].trim().toLowerCase()

const alphaInfo = (mode, extra = {}) => ({
  mode,
  hasAlpha: mode !== MEDIA_ALPHA_OPAQUE,
  scanned: false,
  ...extra,
})

export const UNKNOWN_MEDIA_ALPHA_INFO = alphaInfo(MEDIA_ALPHA_UNKNOWN, {
  reason: 'unknown',
})

export function getAssetMime(asset) {
  return normalizeMime(asset?._asset?.mime || asset?.mime || asset?.blob?.type)
}

export function getInitialMediaAlphaInfo(assetOrMime) {
  const mime = typeof assetOrMime === 'string'
    ? normalizeMime(assetOrMime)
    : getAssetMime(assetOrMime)

  if (
    mime === 'image/jpeg' ||
    mime === 'image/jpg' ||
    mime === 'image/bmp'
  ) {
    return alphaInfo(MEDIA_ALPHA_OPAQUE, {
      mime,
      reason: 'mime-opaque',
    })
  }

  if (mime.startsWith('video/')) {
    return alphaInfo(MEDIA_ALPHA_OPAQUE, {
      mime,
      reason: 'video-default-opaque',
    })
  }

  return alphaInfo(MEDIA_ALPHA_UNKNOWN, {
    mime,
    reason: 'requires-alpha-scan',
  })
}

export function getAssetMediaAlphaInfo(asset) {
  return asset?._asset?.mediaAlphaInfo || asset?.mediaAlphaInfo || null
}

export function setAssetMediaAlphaInfo(asset, info) {
  if (!asset || !info) return info
  if (asset._asset) asset._asset.mediaAlphaInfo = info
  asset.mediaAlphaInfo = info
  return info
}

function waitForImageReady(image) {
  if (!image) return Promise.resolve()
  const ready = image.complete && (image.naturalWidth || image.width)
  if (ready) return Promise.resolve()

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      image.removeEventListener?.('load', onLoad)
      image.removeEventListener?.('error', onError)
    }
    const onLoad = () => {
      cleanup()
      resolve()
    }
    const onError = (err) => {
      cleanup()
      reject(err)
    }

    image.addEventListener?.('load', onLoad, { once: true })
    image.addEventListener?.('error', onError, { once: true })
  })
}

function scanAlphaData(data) {
  let hasTransparent = false
  let hasOpaque = false
  let hasSoft = false

  for (let i = 3; i < data.length; i += 4) {
    const a = data[i]
    if (a === 255) {
      hasOpaque = true
    } else if (a === 0) {
      hasTransparent = true
    } else {
      hasSoft = true
      break
    }
  }

  if (hasSoft) return MEDIA_ALPHA_SOFT
  if (hasTransparent && hasOpaque) return MEDIA_ALPHA_CUTOUT
  if (hasTransparent) return MEDIA_ALPHA_CUTOUT
  return MEDIA_ALPHA_OPAQUE
}

export async function classifyImageAlpha({
  image = null,
  canvas = null,
  width = null,
  height = null,
  mime = '',
} = {}) {
  const initial = getInitialMediaAlphaInfo(mime)
  if (initial.mode === MEDIA_ALPHA_OPAQUE) {
    await waitForImageReady(image)
    return {
      ...initial,
      scanned: false,
    }
  }

  try {
    await waitForImageReady(image)

    const source = canvas || image
    const sourceWidth = Number(width) || Number(source?.naturalWidth) || Number(source?.videoWidth) || Number(source?.width) || 0
    const sourceHeight = Number(height) || Number(source?.naturalHeight) || Number(source?.videoHeight) || Number(source?.height) || 0
    if (!source || sourceWidth <= 0 || sourceHeight <= 0) {
      return {
        ...UNKNOWN_MEDIA_ALPHA_INFO,
        mime: normalizeMime(mime),
        reason: 'missing-source-dimensions',
      }
    }

    const scanScale = Math.min(1, Math.sqrt(MAX_SCAN_PIXELS / Math.max(1, sourceWidth * sourceHeight)))
    const scanWidth = Math.max(1, Math.round(sourceWidth * scanScale))
    const scanHeight = Math.max(1, Math.round(sourceHeight * scanScale))
    const scanCanvas = document.createElement('canvas')
    scanCanvas.width = scanWidth
    scanCanvas.height = scanHeight
    const ctx = scanCanvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) {
      return {
        ...UNKNOWN_MEDIA_ALPHA_INFO,
        mime: normalizeMime(mime),
        reason: 'canvas-context-unavailable',
      }
    }

    ctx.clearRect(0, 0, scanWidth, scanHeight)
    ctx.drawImage(source, 0, 0, scanWidth, scanHeight)
    const data = ctx.getImageData(0, 0, scanWidth, scanHeight).data
    const mode = scanAlphaData(data)

    return alphaInfo(mode, {
      mime: normalizeMime(mime),
      scanned: true,
      width: sourceWidth,
      height: sourceHeight,
      scanWidth,
      scanHeight,
      reason: 'pixel-alpha-scan',
    })
  } catch (err) {
    return {
      ...UNKNOWN_MEDIA_ALPHA_INFO,
      mime: normalizeMime(mime),
      reason: err?.message || 'alpha-scan-failed',
    }
  }
}
